export interface QuarterlyTaxEstimate {
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  period: string;
  dueDate: string;
  income: number;
  expenses: number;
  netIncome: number;
  selfEmploymentTax: number;
  incomeTax: number;
  totalTaxDue: number;
  isPaid: boolean;
}

export interface TaxBracket {
  min: number;
  max: number;
  rate: number;
}

// Tax Brackets by Year (Single Filer)
// For simplicity, we'll use 2024 brackets as base and add modest inflation adjustment for future years
const TAX_BRACKETS_2024: TaxBracket[] = [
  { min: 0, max: 11600, rate: 0.10 },
  { min: 11600, max: 47150, rate: 0.12 },
  { min: 47150, max: 100525, rate: 0.22 },
  { min: 100525, max: 191950, rate: 0.24 },
  { min: 191950, max: 243725, rate: 0.32 },
  { min: 243725, max: 609350, rate: 0.35 },
  { min: 609350, max: Infinity, rate: 0.37 }
];

// 2025 Tax Brackets (estimated with inflation adjustment)
const TAX_BRACKETS_2025: TaxBracket[] = [
  { min: 0, max: 11925, rate: 0.10 },
  { min: 11925, max: 48475, rate: 0.12 },
  { min: 48475, max: 103350, rate: 0.22 },
  { min: 103350, max: 197300, rate: 0.24 },
  { min: 197300, max: 250525, rate: 0.32 },
  { min: 250525, max: 626350, rate: 0.35 },
  { min: 626350, max: Infinity, rate: 0.37 }
];

function getTaxBrackets(year: number): TaxBracket[] {
  if (year >= 2025) return TAX_BRACKETS_2025;
  return TAX_BRACKETS_2024;
}

const STANDARD_DEDUCTION_2024 = 14600;
const STANDARD_DEDUCTION_2025 = 15000;

function getStandardDeduction(year: number): number {
  if (year >= 2025) return STANDARD_DEDUCTION_2025;
  return STANDARD_DEDUCTION_2024;
}
const SELF_EMPLOYMENT_TAX_RATE = 0.153; // 15.3% (Social Security + Medicare)
const SELF_EMPLOYMENT_DEDUCTION = 0.9235; // 92.35% of net earnings subject to SE tax

export function calculateIncomeTax(taxableIncome: number, year: number = new Date().getFullYear()): number {
  let tax = 0;
  const brackets = getTaxBrackets(year);

  for (const bracket of brackets) {
    if (taxableIncome > bracket.min) {
      const taxableInBracket = Math.min(taxableIncome, bracket.max) - bracket.min;
      tax += taxableInBracket * bracket.rate;
    }
    if (taxableIncome <= bracket.max) break;
  }

  return tax;
}

export function calculateSelfEmploymentTax(netIncome: number): number {
  const seTaxableIncome = netIncome * SELF_EMPLOYMENT_DEDUCTION;
  return seTaxableIncome * SELF_EMPLOYMENT_TAX_RATE;
}

export function getQuarterDates(year: number = new Date().getFullYear()): Array<{ quarter: string; period: string; dueDate: string }> {
  return [
    { quarter: 'Q1', period: 'Jan 1 - Mar 31', dueDate: `Apr 15, ${year}` },
    { quarter: 'Q2', period: 'Apr 1 - May 31', dueDate: `Jun 17, ${year}` },
    { quarter: 'Q3', period: 'Jun 1 - Aug 31', dueDate: `Sep 16, ${year}` },
    { quarter: 'Q4', period: 'Sep 1 - Dec 31', dueDate: `Jan 15, ${year + 1}` }
  ];
}

export function calculateQuarterlyEstimates(
  totalIncome: number,
  totalExpenses: number,
  year: number = new Date().getFullYear()
): QuarterlyTaxEstimate[] {
  const netIncome = totalIncome - totalExpenses;
  const quarterDates = getQuarterDates(year);
  const standardDeduction = getStandardDeduction(year);
  
  // Divide income/expenses evenly across 4 quarters (simplified)
  const quarterlyIncome = totalIncome / 4;
  const quarterlyExpenses = totalExpenses / 4;
  const quarterlyNetIncome = netIncome / 4;

  return quarterDates.map(({ quarter, period, dueDate }) => {
    // Self-employment tax
    const seTax = calculateSelfEmploymentTax(quarterlyNetIncome);
    const seDeduction = seTax / 2; // Deduct half of SE tax

    // Income tax (after SE deduction and standard deduction)
    const adjustedIncome = Math.max(0, quarterlyNetIncome - seDeduction - (standardDeduction / 4));
    const incomeTax = calculateIncomeTax(adjustedIncome, year);

    return {
      quarter: quarter as 'Q1' | 'Q2' | 'Q3' | 'Q4',
      period,
      dueDate,
      income: quarterlyIncome,
      expenses: quarterlyExpenses,
      netIncome: quarterlyNetIncome,
      selfEmploymentTax: seTax,
      incomeTax: incomeTax,
      totalTaxDue: seTax + incomeTax,
      isPaid: false
    };
  });
}

export function getNextQuarterDue(): { quarter: string; dueDate: string; daysUntil: number } | null {
  const now = new Date();
  const year = now.getFullYear();
  const quarters = getQuarterDates(year);

  for (const quarter of quarters) {
    const dueDate = new Date(quarter.dueDate);
    if (dueDate > now) {
      const daysUntil = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return {
        quarter: quarter.quarter,
        dueDate: quarter.dueDate,
        daysUntil
      };
    }
  }

  // If all quarters passed, return Q1 of next year
  const nextYearQ1 = getQuarterDates(year + 1)[0];
  const dueDate = new Date(nextYearQ1.dueDate);
  const daysUntil = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  return {
    quarter: nextYearQ1.quarter,
    dueDate: nextYearQ1.dueDate,
    daysUntil
  };
}
